import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./CustomTable.css";

/**
 * CustomTable — the shared page table.
 *
 * Replaces the antd `<Table>` wrapper that used to live at
 * src/views/Common/CustomTable.js. Plain React and CSS, no table library: the
 * markup, the scroll frame and the pager are the ones from the Dashboards /
 * Reports list, which is the reference design for every list page.
 *
 * Deliberately NOT for the report widgets or the dashboard editor — those keep
 * ReportGrid / AdvancedGroupingTable.
 *
 * ── Why two tables ──
 * The column header and the rows are separate <table>s in separate boxes, and
 * only the rows box scrolls. A single table with `position: sticky` headers
 * pins the header fine, but the scroll region is then the whole card, so the
 * scrollbar runs the full height of it — up the side of the header and down to
 * the pager. Splitting them confines the scrollbar to the rows, between the
 * header and the pager.
 *
 * The cost is that the two tables have to agree on column widths, and the
 * widths have to come from somewhere. They come from the rows table: it carries
 * a zero-height sizer row holding the header labels, so its automatic layout
 * accounts for both the header text and the cell content. Those measured
 * widths are then pinned onto the header table. See `measure` below.
 *
 * ── Props ──
 * The prop names are the ones the old wrapper took (`tableHeaders`,
 * `tableData`, `tableClass`, `hasExpandedRows`) so the existing call sites did
 * not have to change, with `columns` / `dataSource` accepted as aliases.
 *
 * Column config — a subset of antd's, so the old column arrays still work:
 *   {
 *     title,                       // string or node
 *     dataIndex,                   // string, dotted path, or array path
 *     key,                         // react key; falls back to dataIndex
 *     render: (value, row, index) => node,
 *     width,                       // css width for the column
 *     align: "left" | "center" | "right",
 *     nowrap: bool,                // never wrap this cell
 *     muted: bool,                 // the softer secondary-text tone
 *     className, headerClassName,
 *   }
 *
 * antd-only keys (`filters`, `filterSearch`, `sorter`, …) are ignored rather
 * than breaking — header filtering is done by the page's own controls now.
 */

/* Reads `dataIndex` off a row. Accepts "name", "a.b.c" and ["a","b","c"]. */
function readCell(row, dataIndex) {
  if (row == null || dataIndex == null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : String(dataIndex).split(".");
  return path.reduce((acc, seg) => (acc == null ? acc : acc[seg]), row);
}

function resolveRowKey(row, index, rowKey) {
  if (typeof rowKey === "function") return rowKey(row, index);
  if (typeof rowKey === "string" && row && row[rowKey] != null) return row[rowKey];
  if (row && row.key != null) return row.key;
  if (row && row.id != null) return row.id;
  return index;
}

function columnClasses(col, base) {
  return [
    base,
    col.align === "center" ? "ctbl-align-center" : "",
    col.align === "right" ? "ctbl-align-right" : "",
    col.nowrap ? "ctbl-nowrap" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/* Chevron for the expand toggle — rotated by CSS when the row is open. */
const CaretIcon = () => (
  <svg className="ctbl-expand-icon" viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true">
    <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Pager ───────────────────────────────────────────────
   Same window as the dashboard list: every page up to 7, then first / last with
   a moving band of three around the current page and ellipses either side. */
function buildPageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_unused, i) => i + 1);
  }
  const pages = [1];
  if (currentPage > 3) pages.push("...");
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (currentPage < totalPages - 2) pages.push("...");
  pages.push(totalPages);
  return pages;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 15, 20];

const CustomTable = (props) => {
  const {
    tableHeaders,
    columns,
    tableData,
    dataSource,
    tableClass = "",
    hasExpandedRows = false,
    rowKey,
    loading = false,
    skeletonRows = 6,
    emptyText = "No records found.",
    minWidth = 700,
    onRowClick,
    rowClassName,
    /* Pagination: `false` turns it off, otherwise `{ pageSize, showInfo,
       showSizeChanger, pageSizeOptions }`. The page can be driven from outside
       with `page` + `onPageChange`. */
    pagination = true,
    pageSize: pageSizeProp,
    pageSizeOptions,
    showSizeChanger,
    page: pageProp,
    onPageChange,
    onPageSizeChange,
    /* Change this — a filter string, a search term, anything — and the table
       jumps back to page 1. Filtering a list you are three pages into
       otherwise leaves you looking at an empty page. */
    resetPageOn,
  } = props;

  const headers = useMemo(() => tableHeaders || columns || [], [tableHeaders, columns]);
  const rows = useMemo(() => tableData || dataSource || [], [tableData, dataSource]);

  const paginationConfig = pagination && typeof pagination === "object" ? pagination : {};
  const paginationEnabled = pagination !== false;
  const showInfo = paginationConfig.showInfo !== false;
  const sizeOptions = pageSizeOptions || paginationConfig.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
  const sizeChangerEnabled =
    showSizeChanger !== undefined
      ? showSizeChanger
      : paginationConfig.showSizeChanger !== undefined
        ? paginationConfig.showSizeChanger
        : true;

  const initialPageSize = pageSizeProp || paginationConfig.pageSize || sizeOptions[0] || 10;

  const [internalPage, setInternalPage] = useState(1);
  /* Rows per page is the reader's choice, so it lives here. The prop is only
     the starting value; a caller that wants to know can pass onPageSizeChange. */
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const isControlled = pageProp != null;
  const currentPage = isControlled ? pageProp : internalPage;

  const setPage = useCallback(
    (next) => {
      if (!isControlled) setInternalPage(next);
      if (onPageChange) onPageChange(next);
    },
    [isControlled, onPageChange],
  );

  const totalRows = rows.length;
  const totalPages = paginationEnabled ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;

  // A caller that changes the initial size prop means it.
  useEffect(() => {
    setPageSizeState(initialPageSize);
  }, [initialPageSize]);

  useEffect(() => {
    if (isControlled) return;
    setInternalPage(1);
  }, [resetPageOn, isControlled]);

  /* Deleting the last row of the last page would otherwise strand the view on a
     page that no longer exists. */
  useEffect(() => {
    if (isControlled) return;
    setInternalPage((p) => Math.min(p, totalPages));
  }, [totalPages, isControlled]);

  const handlePageSizeChange = (next) => {
    setPageSizeState(next);
    // Row 1 of the new size — page 3 of 10-per-page is off the end at 20.
    setPage(1);
    if (onPageSizeChange) onPageSizeChange(next);
  };

  const startIdx = paginationEnabled ? (currentPage - 1) * pageSize : 0;
  const endIdx = paginationEnabled ? startIdx + pageSize : totalRows;
  const visibleRows = paginationEnabled ? rows.slice(startIdx, endIdx) : rows;

  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((keys) => (keys.indexOf(key) !== -1 ? keys.filter((k) => k !== key) : keys.concat(key)));
  }, []);

  // +1 for the caret column, so the expanded panel spans the full width.
  const columnCount = headers.length + (hasExpandedRows ? 1 : 0);

  /* ── Column width sync ───────────────────────────────────
     The rows table lays itself out automatically, sized by its content and by
     the invisible sizer row carrying the header labels. Those settled widths
     are read back and pinned onto the header table, which is then laid out
     `fixed` so it cannot disagree.

     Only the header table is written to, never the rows table — so a measure
     can never change what it just measured, and this cannot oscillate. */
  const headWrapRef = useRef(null);
  const bodyWrapRef = useRef(null);
  const sizerRowRef = useRef(null);
  const [colWidths, setColWidths] = useState(null);

  const measure = useCallback(() => {
    const row = sizerRowRef.current;
    if (!row || !row.cells || row.cells.length === 0) return;
    const next = Array.prototype.map.call(row.cells, (cell) => cell.getBoundingClientRect().width);
    setColWidths((prev) => {
      if (prev && prev.length === next.length && prev.every((w, i) => Math.abs(w - next[i]) < 0.5)) {
        return prev; // unchanged — do not re-render
      }
      return next;
    });
  }, []);

  // After every render: the rows may have changed, and with them the widths.
  useLayoutEffect(() => {
    measure();
  });

  // And whenever the box itself changes size, without a render to prompt it.
  useEffect(() => {
    const target = bodyWrapRef.current;
    let observer = null;
    if (target && typeof window !== "undefined" && typeof window.ResizeObserver === "function") {
      observer = new window.ResizeObserver(measure);
      observer.observe(target);
    }
    window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  /* The header box has its own overflow hidden; scrolling the rows sideways
     drags it along, so the two never come apart. */
  const handleBodyScroll = (e) => {
    if (headWrapRef.current) headWrapRef.current.scrollLeft = e.currentTarget.scrollLeft;
  };

  /* Pinned to the rows table's real width rather than to 100%, so that when the
     rows box grows a vertical scrollbar the header stops short of it instead of
     stretching over it and knocking every column out of line. */
  const totalWidth = colWidths ? colWidths.reduce((sum, w) => sum + w, 0) : null;

  const shellClass = ["ctbl", paginationEnabled ? "" : "ctbl--no-pager", tableClass].filter(Boolean).join(" ");

  const renderColGroup = () =>
    colWidths ? (
      <colgroup>
        {colWidths.map((w, i) => (
          <col key={i} style={{ width: `${w}px` }} />
        ))}
      </colgroup>
    ) : null;

  /* The sizer row. Zero height and transparent, but it carries the header
     labels at the header's own type size, so the automatic layout widens a
     column that would otherwise be narrower than its own title. */
  const renderSizerRow = () => (
    <tr className="ctbl-sizer-row" ref={sizerRowRef} aria-hidden="true">
      {hasExpandedRows && <td className="ctbl-sizer-cell ctbl-sizer-cell-expand" />}
      {/* `width` from the column config is applied here, not on the <th>: the
          rows table is what decides the widths, so a column that asks for one
          has to ask this table. */}
      {headers.map((col, c) => (
        <td
          key={col.key || col.dataIndex || c}
          className="ctbl-sizer-cell"
          style={col.width ? { width: col.width } : undefined}
        >
          {typeof col.title === "string" ? col.title : ""}
        </td>
      ))}
    </tr>
  );

  const renderBody = () => {
    if (loading) {
      return Array.from({ length: skeletonRows }).map((_unused, r) => (
        <tr key={`ctbl-sk-${r}`} className="ctbl-tr">
          {hasExpandedRows && (
            <td className="ctbl-td ctbl-td-expand">
              <span className="ctbl-sk ctbl-sk-line" style={{ width: 14 }} />
            </td>
          )}
          {headers.map((col, c) => (
            <td key={col.key || col.dataIndex || c} className="ctbl-td">
              {/* Widths vary a little per column so the block does not read as a grid. */}
              <span className="ctbl-sk ctbl-sk-line" style={{ width: c === 0 ? "60%" : `${45 + ((c * 13) % 30)}%` }} />
            </td>
          ))}
        </tr>
      ));
    }

    if (visibleRows.length === 0) {
      return (
        <tr>
          <td className="ctbl-empty" colSpan={columnCount}>
            {emptyText}
          </td>
        </tr>
      );
    }

    return visibleRows.map((row, i) => {
      const index = startIdx + i;
      const key = resolveRowKey(row, index, rowKey);
      const isExpanded = expandedKeys.indexOf(key) !== -1;
      const canExpand = hasExpandedRows && row && row.expandedData != null;
      const extraClass = typeof rowClassName === "function" ? rowClassName(row, index) : rowClassName;

      const handleClick = () => {
        if (canExpand) toggleExpanded(key);
        if (onRowClick) onRowClick(row, index);
      };

      return (
        <React.Fragment key={key}>
          <tr
            className={["ctbl-tr", canExpand || onRowClick ? "ctbl-tr-clickable" : "", extraClass || ""]
              .filter(Boolean)
              .join(" ")}
            onClick={canExpand || onRowClick ? handleClick : undefined}
          >
            {hasExpandedRows && (
              <td className="ctbl-td ctbl-td-expand">
                {canExpand && (
                  <button
                    type="button"
                    className="ctbl-expand-btn"
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? "Collapse row" : "Expand row"}
                    onClick={(e) => {
                      // The row itself already toggles; without this the button
                      // would toggle a second time and cancel itself out.
                      e.stopPropagation();
                      toggleExpanded(key);
                    }}
                  >
                    <CaretIcon />
                  </button>
                )}
              </td>
            )}
            {headers.map((col, c) => {
              const value = readCell(row, col.dataIndex);
              const content = col.render ? col.render(value, row, index) : value;
              return (
                <td
                  key={col.key || col.dataIndex || c}
                  className={columnClasses(
                    col,
                    ["ctbl-td", col.muted ? "ctbl-muted" : "", col.className || ""].filter(Boolean).join(" "),
                  )}
                >
                  {content}
                </td>
              );
            })}
          </tr>
          {canExpand && isExpanded && (
            <tr className="ctbl-expanded-row">
              <td className="ctbl-expanded-cell" colSpan={columnCount}>
                {row.expandedData}
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  const renderPagination = () => {
    if (!paginationEnabled) return null;
    // Nothing to page through, and no counts worth showing.
    if (loading || totalRows === 0) return null;

    return (
      <div className="ctbl-pagination">
        <div className="ctbl-pagination-left">
          {showInfo && (
            <span className="ctbl-pagination-info">
              Showing {startIdx + 1} to {Math.min(endIdx, totalRows)} of {totalRows} results
            </span>
          )}
          {sizeChangerEnabled && (
            <label className="ctbl-page-size">
              <span className="ctbl-page-size-label">Show</span>
              <select
                className="ctbl-page-size-select"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                aria-label="Rows per page"
              >
                {sizeOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="ctbl-pagination-btns">
          <button
            type="button"
            className="ctbl-page-btn"
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            ‹
          </button>
          {buildPageNumbers(currentPage, totalPages).map((p, i) =>
            p === "..." ? (
              <span key={`ctbl-ellipsis-${i}`} className="ctbl-page-ellipsis">
                …
              </span>
            ) : (
              <button
                type="button"
                key={p}
                className={`ctbl-page-btn ${currentPage === p ? "ctbl-page-active" : ""}`}
                onClick={() => setPage(p)}
                aria-current={currentPage === p ? "page" : undefined}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            className="ctbl-page-btn"
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={shellClass}>
      {/* Header — outside the scroll box, so the scrollbar never runs past it.
          Kept in step with the rows sideways by handleBodyScroll. */}
      <div className="ctbl-head-wrap" ref={headWrapRef}>
        <table
          className="ctbl-table ctbl-table--head"
          style={{ minWidth, width: totalWidth ? `${totalWidth}px` : "100%" }}
        >
          {renderColGroup()}
          <thead className="ctbl-thead">
            <tr>
              {hasExpandedRows && <th className="ctbl-th ctbl-th-expand" aria-label="Expand" />}
              {headers.map((col, c) => (
                <th
                  key={col.key || col.dataIndex || c}
                  className={columnClasses(col, ["ctbl-th", col.headerClassName || ""].filter(Boolean).join(" "))}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.title}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>

      {/* The only scroll region: the rows, between the header and the pager.
          data-drag-scroll: they pan by hand as well as by wheel —
          see src/utils/dragToScroll.js */}
      <div className="ctbl-body-wrap" ref={bodyWrapRef} onScroll={handleBodyScroll} data-drag-scroll>
        <table className="ctbl-table ctbl-table--body" style={{ minWidth }}>
          <tbody>
            {renderSizerRow()}
            {renderBody()}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
};

export default CustomTable;
